import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Swal from "sweetalert2";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Radio from "@mui/material/Radio";
import axios from "axios";
import EmailSendingLoader from "../utils/LoadingForEmailSending";
import GlobalSnackbar from "../utils/GlobalSnackbar";
import CapitalizeFirstLetter from "../utils/CapitalizeFirstLetter";

const ClientMembersModal = ({
  open,
  onClose,
  organizationId,
  isEnquiryData,
  selectedStatus,
  getRfpList,
  createCampaignFromRfp,
  setSnackbar,
  selectedMembers,
  setSelectedMembers,
  kamData,
  snackbar,
}) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isClientRep, setClientRep] = useState([]);
  const [assignAdmins, setAssignAdmins] = useState([]);

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const orderConfPhases = [
    {
      id: 1,
      phaseName: "assignClientRep",
      title: "Select Client Servicing Manager",
      subHeader: "",
      list: assignAdmins,
    },
    {
      id: 2,
      phaseName: "welcomeMailToClients",
      title: "Client Manager",
      subHeader: "Select client for welcome mail",
      list: members,
    },
  ];

  const [index, setIndex] = useState(0);
  const handleSingleSelect = async (member) => {
    setClientRep([member]); // overwrite

    const res = await axios.put(
      `${serverURL}/requestForProposal/editRfp/${isEnquiryData?._id}`,
      {
        clientRepresentative: member.id, // field name to store in backend
        emailAddress: isEnquiryData?.campaignOwnerId.emailAddress,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  };

  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");
  const getRoleStyles = (roleId, isSelected) => {
    switch (roleId) {
      case 1: // Super Admin
        return {
          background: isSelected
            ? "linear-gradient(135deg, #ff416c, #ff4b2b)"
            : "rgba(255, 65, 108, 0.12)",
          color: isSelected ? "#fff" : "#ff416c",
          boxShadow: isSelected ? "0 2px 8px rgba(255, 65, 108, 0.4)" : "none",
        };

      case 2: // Admin
        return {
          background: isSelected
            ? "linear-gradient(135deg, #0072ff, #00c6ff)"
            : "rgba(0, 114, 255, 0.12)",
          color: isSelected ? "#fff" : "#0072ff",
          boxShadow: isSelected ? "0 2px 8px rgba(0, 114, 255, 0.4)" : "none",
        };

      default: // Team Member
        return {
          background: isSelected
            ? "linear-gradient(135deg, #11998e, #38ef7d)"
            : "rgba(17, 153, 142, 0.12)",
          color: isSelected ? "#fff" : "#11998e",
          boxShadow: isSelected ? "0 2px 8px rgba(17, 153, 142, 0.4)" : "none",
        };
    }
  };
  const onNext = () => {
    if (index == 0 && isClientRep.length == 0) {
      setSnackbar({
        open: true,
        message: "Select one Client Servicing Manager",
        severity: "error",
      });
      return;
    }
    if (index < orderConfPhases.length - 1) {
      setIndex((prev) => prev + 1);
    }
  };
  const onBack = () => {
    if (index > 0) {
      setIndex((prev) => prev - 1);
    }
  };
  const fetchMembers = async () => {
    try {
      const res = await axios.get(
        `${serverURL}/organization/view/${organizationId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setMembers(res?.data?.clientUsers);
    } catch (err) {
      console.error("Error fetching members:", err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on open
  useEffect(() => {
    const fetchAdmins = isEnquiryData.keyAccountManagerId;

    const checkAdminsData = kamData
      .map((ele) => {
        if (fetchAdmins?.includes(ele._id) && ele.role == 0) {
          return {
            emailAddress: ele.email,
            name: ele.name,
            userRoleId: ele.role,
            id: ele._id,
            profilePhoto: ele.profilePhoto,
          };
        }
      })
      .filter(Boolean);
    setAssignAdmins(checkAdminsData);

    fetchMembers();
  }, []);

  // Handle checkbox toggle
  const handleToggle = (member, fromWhere) => {
    setSelectedMembers((prev) => {
      const exists = prev.some((m) => m.emailAddress === member.emailAddress);
      if (exists) {
        // remove if already selected
        return prev.filter((m) => m.emailAddress !== member.emailAddress);
      } else {
        // add new selected member
        return [
          ...prev,
          {
            name: member.name,
            emailAddress: member.emailAddress,
            id: member._id,
          },
        ];
      }
    });
  };

  // Handle submit with confirmation
  const handleSubmit = async () => {
    if (selectedMembers.length === 0) {
      Swal.fire({
        customClass: { container: "my-swal" },
        title: "No Members Selected",
        text: "Please select at least one member.",
        icon: "warning",
      });
      return;
    }

    setLoading(true);

    try {
      await createCampaignFromRfp(isEnquiryData);
    } catch (campaignError) {
      Swal.fire({
        customClass: { container: "my-swal" },
        title: "Campaign Creation Failed",
        text:
          campaignError?.response?.data?.message ||
          campaignError.message ||
          "Campaign could not be created.",
        icon: "error",
      });
      return;
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      BackdropProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.95)", // darker black overlay
        },
      }}
      PaperProps={{
        sx: {
          height: { xs: "80vh", sm: "75vh" },
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {loading ? (
        <>
          <EmailSendingLoader />
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              backgroundColor: "#fff",
              borderBottom: "1px solid #ddd",
              position: "sticky",
              top: 0,
              zIndex: 2,
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {orderConfPhases[index]?.title}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontSize: "0.9rem",
              }}
            >
              {orderConfPhases[index]?.subHeader}
            </Typography>
          </DialogTitle>

          {/* Scrollable Content */}
          <DialogContent
            sx={{
              flex: 1,
              overflowY: "auto",
              backgroundColor: "#fafafa",
              padding: 2,
              position: "relative",
            }}
          >
            {orderConfPhases[0]?.list?.length == 0 && (
              <div className="w-full flex justify-center items-center">
                <p>
                  Please ensure a CSM is assigned to the enquiry before
                  advancing to the next phase.
                </p>
              </div>
            )}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 1 }}
            >
              {orderConfPhases[index].list?.map((member, i) => {
                let isSelected;

                if (index == 1) {
                  isSelected = selectedMembers.some(
                    (m) => m.emailAddress === member.emailAddress,
                  );
                }
                if (index == 0) {
                  isSelected = isClientRep.some(
                    (m) => m.emailAddress === member.emailAddress,
                  );
                }

                return (
                  <Box
                    index={i}
                    key={member.emailAddress}
                    onClick={() =>
                      index === 0
                        ? handleSingleSelect(member)
                        : handleToggle(member, "fromParent")
                    }
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderRadius: "12px",
                      cursor: "pointer",
                      px: 2,
                      py: 1.5,
                      border: isSelected
                        ? "2px solid #1976D2"
                        : "1px solid #ddd",

                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                  >
                    {/* Gradient background when selected */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: isSelected ? "100%" : "0%",
                        background: "linear-gradient(90deg, #1976D2, #fff)",
                        zIndex: 0,
                        transition: "width 0.5s ease",
                      }}
                    />

                    {/* Checkbox + Info */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        position: "relative",
                        zIndex: 1,
                        color: isSelected ? "#fff" : "#333",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "start",
                          justifyContent: "center",
                          gap: "5px",
                        }}
                      >
                        {index == 0 && (
                          <div className="w-16 h-16 rounded-full border border-1 overflow-hidden">
                            {member.profilePhoto ? (
                              <img
                                src={member?.profilePhoto}
                                alt=""
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              <img
                                src="/assests/AdminDashboard/userForIp.webp"
                                alt="ProfilePhoto"
                              />
                            )}
                          </div>
                        )}
                        <Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: isSelected ? "#fff" : "#333",
                            }}
                          >
                            {CapitalizeFirstLetter(member.name)}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: isSelected
                                ? "rgba(255,255,255,0.9)"
                                : "#555",
                              fontSize: "0.85rem",
                            }}
                          >
                            {member.emailAddress}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            px: 2,
                            py: 0.6,
                            borderRadius: "20px", // pill style
                            fontSize: "0.75rem",
                            letterSpacing: "0.5px",
                            textTransform: "uppercase",
                            transition: "all 0.3s ease",
                            ...getRoleStyles(member.userRoleId, isSelected),
                          }}
                        >
                          {member.userRoleId === 1
                            ? "Super Admin"
                            : member.userRoleId === 2
                              ? "Admin"
                              : member.userRoleId == 0
                                ? "CSM"
                                : "Team Member"}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        control={
                          index === 0 ? (
                            <Radio
                              checked={isSelected}
                              onClick={(e) => e.stopPropagation()} // stop parent click
                              onChange={(e) => {
                                handleSingleSelect(member);
                              }}
                              icon={<RadioButtonUncheckedIcon />}
                              checkedIcon={<CheckCircleIcon />}
                              sx={{
                                color: "#1976D2",
                                "&.Mui-checked": { color: "#1976D2" },
                              }}
                            />
                          ) : (
                            <Checkbox
                              checked={isSelected}
                              onClick={(e) => e.stopPropagation()} // stop parent click
                              onChange={() => handleToggle(member, "fromChild")}
                              icon={<CheckBoxOutlineBlankIcon />}
                              checkedIcon={<CheckBoxIcon />}
                              sx={{
                                color: "#1976D2",
                                "&.Mui-checked": { color: "#1976D2" },
                              }}
                            />
                          )
                        }
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <GlobalSnackbar
              snackbar={snackbar}
              handleClose={handleSnackbarClose}
            />
          </DialogContent>
          <div className="w-full flex justify-center items-center ">
            {orderConfPhases.map((ele, ind) => {
              return (
                <p
                  className={` ${index === ind ? "w-8" : "w-2"} h-2 bg-gray-300 m-1 rounded-full  `}
                ></p>
              );
            })}
          </div>

          {/* Fixed Footer */}
          <DialogActions
            sx={{
              backgroundColor: "#fff",
              p: 2,
              position: "sticky",
              bottom: 0,
              zIndex: 2,
              display: "flex",
              width: "100%",
              gap: 2,
            }}
          >
            {index == 0 ? (
              <>
                <Button
                  onClick={onClose}
                  disabled={loading}
                  sx={{ width: "50%", padding: "10px", borderRadius: "20px" }}
                  variant="outlined"
                >
                  Cancel
                </Button>

                <Button
                  onClick={onNext}
                  disabled={loading}
                  sx={{ width: "50%", padding: "10px", borderRadius: "20px" }}
                  variant="contained"
                >
                  Next
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={onBack}
                  disabled={loading}
                  sx={{ width: "50%", padding: "10px", borderRadius: "20px" }}
                  variant="outlined"
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading || selectedMembers.length === 0}
                  sx={{ width: "50%", padding: "10px", borderRadius: "20px" }}
                >
                  Send Email
                </Button>
              </>
            )}
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ClientMembersModal;
